import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useAccessibleApps } from '@bapecom/auth';
import { getDashboardApps, type DashboardApp, type AppStatus } from '@bapecom/app-registry';
import {
  Palette,
  HardDrive,
  Users,
  BarChart3,
  ShoppingBag,
  Settings,
  Search,
  Lock,
  ArrowRight,
  Target,
  type LucideIcon
} from 'lucide-react';

// Map icon names to Lucide components
const ICON_MAP: Record<string, LucideIcon> = {
  Palette,
  HardDrive,
  Users,
  BarChart3,
  ShoppingBag,
  Settings,
  Target,
};

// Helper to get Lucide icon by name
const getLucideIcon = (iconName: string, size: number = 24) => {
  const Icon = ICON_MAP[iconName] || Settings;
  return <Icon size={size} />;
};

// Extended dashboard app with React icon
interface DashboardAppWithIcon extends DashboardApp {
  lucideIconElement: React.ReactNode;
}

// Get apps from registry with React icon elements
const getAppsWithIcons = (): DashboardAppWithIcon[] => {
  return getDashboardApps().map(app => ({
    ...app,
    lucideIconElement: getLucideIcon(app.lucideIcon),
  }));
};

const StatusBadge = ({ status }: { status: AppStatus }) => {
  const styles: Record<AppStatus, string> = {
    live: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    development: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    coming_soon: 'bg-slate-700/50 text-slate-400 border-slate-700',
    deprecated: 'bg-red-500/10 text-red-400 border-red-500/20',
  };

  const labels: Record<AppStatus, string> = {
    live: 'LIVE',
    development: 'In Development',
    coming_soon: 'Coming Soon',
    deprecated: 'Deprecated',
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Get apps from registry (single source of truth)
  const allApps = useMemo(() => getAppsWithIcons(), []);
  
  // Filter accessible apps based on user permissions
  const accessibleApps = useAccessibleApps(allApps) as DashboardAppWithIcon[];
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filters = ['All', 'Personal', 'Management', 'Creative', 'Admin', 'Utility'];

  const filteredApps = accessibleApps.filter(app => {
    const matchesFilter = activeFilter === 'All' || app.category === activeFilter;
    const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          app.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleAppClick = (app: DashboardAppWithIcon) => {
    if (app.status === 'coming_soon') return;
    
    if (app.url.startsWith('http')) {
      window.open(app.url, '_blank');
    } else {
      navigate(app.url);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in duration-500">
      {/* Welcome Section */}
      <div className="mb-10">
        <h1 className="text-4xl font-bold text-white mb-2">Welcome, {user?.name?.split(' ').pop() || user?.name}! 👋</h1>
        <p className="text-slate-400 text-lg">Select an app to start working</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeFilter === filter
                  ? 'bg-white text-slate-900 shadow-lg shadow-white/10'
                  : 'bg-slate-800/50 text-slate-400 hover:bg-slate-800 hover:text-white border border-transparent hover:border-slate-700'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative w-full md:w-80 group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" size={18} />
          <input 
            type="text" 
            placeholder="Search apps..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-900/50 border border-slate-800 text-slate-200 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 focus:bg-slate-900 transition-all placeholder:text-slate-600"
          />
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredApps.map((app) => (
          <div 
            key={app.id}
            onClick={() => handleAppClick(app)}
            className={`group relative bg-slate-800/20 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6 transition-all duration-300 ${
              app.status === 'coming_soon' 
                ? 'opacity-70 cursor-not-allowed' 
                : 'hover:bg-slate-800/40 hover:border-slate-600 hover:-translate-y-1 hover:shadow-xl cursor-pointer'
            }`}
          >
            <div className="flex justify-between items-start mb-6">
              <div className={`p-3.5 rounded-xl bg-gradient-to-br ${app.gradient} shadow-lg text-white`}>
                {app.lucideIconElement}
              </div>
              <StatusBadge status={app.status} />
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-indigo-300 transition-colors">{app.name}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{app.description}</p>
            </div>

            <div className="flex items-center justify-between mt-auto pt-6 border-t border-slate-800/50">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{app.category}</span>
              
              {app.status === 'coming_soon' ? (
                <Lock size={18} className="text-slate-600" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-slate-400 group-hover:bg-indigo-500 group-hover:text-white transition-all duration-300">
                  <ArrowRight size={16} className="-ml-0.5" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredApps.length === 0 && (
        <div className="text-center py-12 bg-slate-800/20 backdrop-blur-md border border-slate-700/50 rounded-2xl">
          <p className="text-slate-400">
            {searchQuery || activeFilter !== 'All' 
              ? 'No matching apps found.' 
              : 'You do not have access to any apps yet.'}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {!searchQuery && activeFilter === 'All' && 'Please contact Admin to get access.'}
          </p>
        </div>
      )}
    </div>
  );
};

